import { useState } from "react";
import { fetchChatAnswer, sendFeedback } from "../api/chat.api";
import { useErrorStore } from "../store/error.store";
import { FlowMessageData, FlowType } from "../types/chat.type";

/**
 * チャットボットの応答
 */
interface ChatbotResponse {
  data: string;
  id?: string;
  shouldDisplayFeedback?: boolean;
  feedback?: string;
  timestamp?: string;
  referLinks?: Array<{
    title: string;
    url: string;
  }>;
  isFlow?: boolean;
  flowData?: FlowMessageData;
}

/**
 * チャットAPI
 */
export function useFetchAnswer() {
  const setError = useErrorStore((state) => state.setError);
  const [answerData, setAnswerData] = useState<ChatbotResponse | null>();
  const [isLoading, setIsLoading] = useState(false);
  const [isAnswering, setIsAnswering] = useState(false);
  const [metadata, setMetadata] = useState<Partial<ChatbotResponse>>({});

  let currentMetadata = {
    id: "",
    shouldDisplayFeedback: false,
    feedback: "",
    referLinks: [],
  };

  /**
   * チャットボットの質問を送信する
   * @param messageId メッセージID
   * @param question 質問
   * @param flowParams フロー情報
   */
  const sendChatbotQuestion = async (
    messageId: string,
    question: string,
    flowParams?: {
      flowType: FlowType;
      flowStepId: string;
      flowButtonId: string;
    },
  ) => {
    setIsLoading(true);
    setIsAnswering(true);
    setAnswerData(null);
    setMetadata({});

    try {
      // チャットボットの質問を送信する
      const stream = await fetchChatAnswer({
        message_id: messageId,
        question: question,
        ...(flowParams && {
          message_type: "flow_choice",
          flow_type: flowParams.flowType,
          flow_step_id: flowParams.flowStepId,
          flow_button_id: flowParams.flowButtonId,
        }),
      });

      if (!stream) {
        setError("サーバエラーで回答に失敗しました");
        return;
      }

      const reader = stream.getReader();
      const decoder = new TextDecoder();
      let done = false;
      let bufferedData = "";

      // ストリームデータをループする
      while (!done) {
        const { value, done: streamDone } = await reader.read();
        done = streamDone;

        if (value) {
          const chunk = decoder.decode(value, { stream: true });
          bufferedData += chunk;
          const lines = bufferedData.split("\n");
          bufferedData = lines.pop() || "";

          for (const line of lines) {
            if (line.trim()) {
              try {
                const parsedData = JSON.parse(line.trim());

                if (
                  parsedData.success &&
                  parsedData.data &&
                  parsedData.data.message_id
                ) {
                  if (!parsedData.success) {
                    setError("サーバエラーで回答に失敗しました");
                    return;
                  }
                  currentMetadata = {
                    id: parsedData.data.message_id,
                    shouldDisplayFeedback:
                      parsedData.data.should_display_feedback,
                    feedback: parsedData.data.feedback,
                    referLinks: parsedData.data.refer_links || [],
                  };
                  setMetadata(currentMetadata);
                } else if (typeof parsedData?.data === "string") {
                  // string判定, 空文字ではflowEndedと見なすため
                  setIsLoading(false);
                  setAnswerData((prev) => {
                    const newData: ChatbotResponse = {
                      data: (prev?.data || "") + parsedData.data,
                      id: currentMetadata.id,
                      shouldDisplayFeedback:
                        currentMetadata.shouldDisplayFeedback,
                      feedback: currentMetadata.feedback,
                      referLinks: currentMetadata.referLinks,
                    };

                    // フローの場合はflowDataを取り込む
                    if (parsedData.isFlow) {
                      newData.isFlow = true;
                      newData.flowData = {
                        flowType: parsedData.flowType,
                        flowStepId: parsedData.flowStepId,
                        flowEnded: parsedData.flowEnded ?? false,
                        choices: parsedData.choices || [],
                        linkButtons: parsedData.linkButtons || [],
                      };
                    }

                    return newData;
                  });
                } else if (parsedData?.data) {
                  if (parsedData.data.type === "message_stop") {
                    setIsAnswering(false);
                    break;
                  }
                } else if (parsedData?.refer_links) {
                  currentMetadata.referLinks = parsedData.refer_links;
                  setMetadata((prev) => ({
                    ...prev,
                    referLinks: parsedData.refer_links,
                  }));
                  setAnswerData((prev) => {
                    if (prev) {
                      return {
                        ...prev,
                        referLinks: parsedData.refer_links,
                      };
                    }
                    return prev;
                  });
                } else {
                  // LLMModelかRetrieve APIよって返されたデータが違うかな
                  console.warn("不明なデータ形式:", parsedData);
                }
              } catch (error) {
                console.error("JSON解析に失敗しました:", error, line);
                setError("データ解析に失敗しました");
              }
            }
          }
        }
      }
      // データがある場合
      if (bufferedData.trim()) {
        try {
          const parsedData = JSON.parse(bufferedData);
          if (parsedData?.data && typeof parsedData.data === "string") {
            setAnswerData((prev) => {
              const newData = {
                ...prev,
                ...metadata,
                data: (prev?.data || "") + parsedData.data,
              };
              return newData;
            });
          }
        } catch (error) {
          console.error("JSON解析に失敗しました:", error, bufferedData);
          setError("データ解析に失敗しました");
        }
      }

      // referLinksが含まれているか
      setAnswerData((prev) => {
        if (prev && currentMetadata.referLinks.length > 0) {
          return {
            ...prev,
            referLinks: currentMetadata.referLinks,
          };
        }
        return prev;
      });
    } catch (error: unknown) {
      console.error("ストリーム処理に失敗しました:", error);
      if (error instanceof Error) {
        setError(error.message);
      } else {
        setError("サーバエラーで回答に失敗しました");
      }
    } finally {
      setIsAnswering(false);
      setIsLoading(false);
    }
  };

  /**
   * フィードバックを送信する
   * @param messageId メッセージID
   * @param isPositive 正しいかどうか
   */
  const handleFeedback = async (messageId: string, isPositive: boolean) => {
    try {
      const result = await sendFeedback({
        feedback_flag: isPositive,
        message_id: messageId,
      });
      return result;
    } catch (error) {
      console.error("フィードバック送信に失敗しました:", error);
      if (error instanceof Error) {
        setError(error.message);
      } else {
        setError("フィードバック送信に失敗しました");
      }
      return { success: false, message: "フィードバック送信に失敗しました" };
    }
  };

  return {
    answerData,
    isLoading,
    isAnswering,
    sendChatbotQuestion,
    handleFeedback,
  };
}
